import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
    ToggleSwitchControllerContext,
    ToggleSwitchControllerPolicy,
    ToggleSwitchControllerContextValue,
} from "../../state/toggle-switch-controller.context";
import { parseToggleSwitchControllerPolicy } from "../../shared/utils/toggle-switch-controller-policy.utils";
import { useActiveTogglesFromUrl } from "../../state/active-toggles-url.context";
import { parseActiveTogglesParam } from "../../shared/utils/active-toggles.utils";

interface ToggleSwitchControllerProps {
    policy: string;
    activeToggles?: string;
    children: React.ReactNode;
}

function collectToggleSwitchNames(children: React.ReactNode): string[] {
    const discovered: string[] = [];
    const walk = (nodes: React.ReactNode): void => {
        React.Children.forEach(nodes, (child) => {
            if (!React.isValidElement<{
                name?: unknown;
                isDefault?: unknown;
                clickAwayBreakpoint?: unknown;
                hiddenBelowBreakpoint?: unknown;
                children?: React.ReactNode;
            }>(child)) {
                return;
            }
            const maybeName = child.props?.name;
            const looksLikeToggleSwitch =
                typeof child.props?.isDefault === "boolean"
                && typeof child.props?.clickAwayBreakpoint === "number"
                && typeof child.props?.hiddenBelowBreakpoint === "number";
            if (looksLikeToggleSwitch && typeof maybeName === "string" && maybeName.trim()) {
                discovered.push(maybeName.trim());
            }
            if (child.props?.children) {
                walk(child.props.children);
            }
        });
    };
    walk(children);
    return discovered;
}

function getInitialOpenNamesFromSignal(
    activeToggleNames: string[],
    discoveredNames: string[],
    policy: ToggleSwitchControllerPolicy,
): Set<string> {
    if (activeToggleNames.length === 0 || discoveredNames.length === 0) {
        return new Set();
    }
    const discoveredSet = new Set(discoveredNames);
    const matchedInSignalOrder = activeToggleNames.filter((name) => discoveredSet.has(name));
    if (matchedInSignalOrder.length === 0) {
        return new Set();
    }
    if (policy === "multi") {
        return new Set(matchedInSignalOrder);
    }
    return new Set([matchedInSignalOrder[0]!]);
}

function subscribeResize(onStoreChange: () => void): () => void {
    window.addEventListener("resize", onStoreChange);
    return () => window.removeEventListener("resize", onStoreChange);
}

function getViewportWidthSnapshot(): number {
    return window.innerWidth;
}

function getViewportWidthServerSnapshot(): undefined {
    return undefined;
}

function reconcileOpenSetForPolicy(
    openNames: Set<string>,
    registeredNames: string[],
    policy: ToggleSwitchControllerPolicy,
): Set<string> {
    if (policy === "multi") {
        return openNames;
    }
    if (registeredNames.length === 0) {
        return new Set();
    }
    if (policy === "zeroOrOne") {
        if (openNames.size <= 1) {
            return openNames;
        }
        const keep = registeredNames.find((name) => openNames.has(name)) ?? registeredNames[0];
        return new Set([keep!]);
    }
    // exactlyOne — AC-2406-3: normalize to first child in registration (document) order when policy becomes exactlyOne
    return new Set([registeredNames[0]!]);
}

export function ToggleSwitchController({ policy, activeToggles, children }: ToggleSwitchControllerProps): JSX.Element {
    const innerWidth = useSyncExternalStore(
        subscribeResize,
        getViewportWidthSnapshot,
        getViewportWidthServerSnapshot,
    );

    const parsedPolicy = useMemo(() => parseToggleSwitchControllerPolicy(policy), [policy]);
    const effectivePolicy = useMemo(
        () => parsedPolicy.resolve(innerWidth),
        [parsedPolicy, innerWidth],
    );
    const effectivePolicyRef = useRef(effectivePolicy);
    effectivePolicyRef.current = effectivePolicy;
    const activeTogglesFromUrl = useActiveTogglesFromUrl();
    const discoveredNames = useMemo(() => collectToggleSwitchNames(children), [children]);
    const initialActiveToggleNamesRef = useRef<string[] | null>(null);
    if (initialActiveToggleNamesRef.current === null) {
        initialActiveToggleNamesRef.current = activeToggles !== undefined
            ? parseActiveTogglesParam(activeToggles)
            : [...activeTogglesFromUrl];
    }
    const activeToggleSetRef = useRef<Set<string>>(new Set(initialActiveToggleNamesRef.current));

    const [registeredNames, setRegisteredNames] = useState<string[]>([]);
    const [openNames, setOpenNames] = useState<Set<string>>(
        () => getInitialOpenNamesFromSignal(initialActiveToggleNamesRef.current ?? [], discoveredNames, effectivePolicy),
    );
    const registeredNamesRef = useRef<string[]>([]);
    registeredNamesRef.current = registeredNames;

    const prevEffectivePolicyRef = useRef<ToggleSwitchControllerPolicy | undefined>(undefined);

    const ensureExactlyOneSelection = useCallback((currentOpen: Set<string>, currentRegistered: string[]): Set<string> => {
        if (currentRegistered.length === 0) {
            return new Set();
        }
        const firstRegistered = currentRegistered[0]!;
        const currentlyOpen = currentRegistered.find((name) => currentOpen.has(name));
        return new Set([currentlyOpen || firstRegistered]);
    }, []);

    useEffect(() => {
        if (prevEffectivePolicyRef.current === undefined) {
            prevEffectivePolicyRef.current = effectivePolicy;
            return;
        }
        if (prevEffectivePolicyRef.current === effectivePolicy) {
            return;
        }
        prevEffectivePolicyRef.current = effectivePolicy;
        setOpenNames((previousOpen) =>
            reconcileOpenSetForPolicy(previousOpen, registeredNamesRef.current, effectivePolicy),
        );
    }, [effectivePolicy]);

    const register = useCallback((name: string, isDefault: boolean) => {
        setRegisteredNames((previous) => {
            if (previous.includes(name)) {
                return previous;
            }
            const next = [...previous, name];
            setOpenNames((previousOpen) => {
                const policy = effectivePolicyRef.current;
                const initialActiveToggleNames = initialActiveToggleNamesRef.current ?? [];
                const hasSignalMatch = activeToggleSetRef.current.has(name);
                if (policy === "multi") {
                    if (!isDefault && !hasSignalMatch) {
                        return previousOpen;
                    }
                    const nextOpen = new Set(previousOpen);
                    nextOpen.add(name);
                    activeToggleSetRef.current.delete(name);
                    return nextOpen;
                }
                if (initialActiveToggleNames.length > 0) {
                    const nextRegisteredForSignal = [...next];
                    const signalSelection = initialActiveToggleNames.find((candidateName) =>
                        nextRegisteredForSignal.includes(candidateName),
                    );
                    if (signalSelection) {
                        return new Set([signalSelection]);
                    }
                }
                if (policy === "zeroOrOne") {
                    if (!isDefault || previousOpen.size > 0) {
                        return previousOpen;
                    }
                    return new Set([name]);
                }
                if (previousOpen.size === 0) {
                    return new Set([name]);
                }
                return ensureExactlyOneSelection(previousOpen, next);
            });
            return next;
        });
    }, [ensureExactlyOneSelection]);

    const unregister = useCallback((name: string) => {
        setRegisteredNames((previousRegistered) => {
            const nextRegistered = previousRegistered.filter((registeredName) => registeredName !== name);
            setOpenNames((previousOpen) => {
                const nextOpen = new Set(previousOpen);
                nextOpen.delete(name);
                if (effectivePolicyRef.current === "exactlyOne") {
                    return ensureExactlyOneSelection(nextOpen, nextRegistered);
                }
                return nextOpen;
            });
            return nextRegistered;
        });
    }, [ensureExactlyOneSelection]);

    const isOpen = useCallback((name: string) => openNames.has(name), [openNames]);
    const isRegistered = useCallback((name: string) => registeredNames.includes(name), [registeredNames]);

    const open = useCallback((name: string) => {
        setOpenNames((previousOpen) => {
            if (effectivePolicyRef.current === "multi") {
                const nextOpen = new Set(previousOpen);
                nextOpen.add(name);
                return nextOpen;
            }
            return new Set([name]);
        });
    }, []);

    const toggle = useCallback((name: string) => {
        setOpenNames((previousOpen) => {
            const policy = effectivePolicyRef.current;
            if (policy === "multi") {
                const nextOpen = new Set(previousOpen);
                if (nextOpen.has(name)) {
                    nextOpen.delete(name);
                } else {
                    nextOpen.add(name);
                }
                return nextOpen;
            }
            if (policy === "zeroOrOne") {
                if (previousOpen.has(name)) {
                    return new Set();
                }
                return new Set([name]);
            }
            // exactlyOne — AC-2406-1: ignore deactivation of the sole active switch
            if (policy === "exactlyOne") {
                if (!previousOpen.has(name)) {
                    return new Set([name]);
                }
                if (previousOpen.size === 1) {
                    return previousOpen;
                }
                return new Set([name]);
            }
            const _exhaustive: never = policy;
            return _exhaustive;
        });
    }, []);

    const close = useCallback((name: string) => {
        setOpenNames((previousOpen) => {
            const policy = effectivePolicyRef.current;
            if (policy === "exactlyOne") {
                return previousOpen;
            }
            if (policy === "zeroOrOne" || policy === "multi") {
                if (!previousOpen.has(name)) {
                    return previousOpen;
                }
                const nextOpen = new Set(previousOpen);
                nextOpen.delete(name);
                return nextOpen;
            }
            const _exhaustive: never = policy;
            return _exhaustive;
        });
    }, []);

    const contextValue = useMemo<ToggleSwitchControllerContextValue>(() => ({
        register,
        unregister,
        isRegistered,
        isOpen,
        open,
        toggle,
        close,
        policy: effectivePolicy,
    }), [close, effectivePolicy, isOpen, isRegistered, open, register, toggle, unregister]);

    return (
        <ToggleSwitchControllerContext.Provider value={contextValue}>
            {children}
        </ToggleSwitchControllerContext.Provider>
    );
}
